import { IConfigComponent, ICompSetting, CommonStringFiltersDto } from '@ts/ui-models';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { IDataGridConfig, TOperator } from '@ts/storybook';

//These functions are to centralise the DataGrid and EntitySelector fetches,
//so that only with the componentId, you can know what you can search/sort/filter.

const getWhere = (configProps: IConfigComponent, componentSetting: ICompSetting, filterModel: any) => {
  let where = configProps.where;
  let like = {};

  const quickFindFieldsToSearch = componentSetting?.quickFindColumns ?? ['logicalId', 'id'];

  if (quickFindFieldsToSearch && filterModel?.quickFilterValues?.[0]) {
    like = {
      $or: quickFindFieldsToSearch.map(key => ({
        [key]: { $iLike: `%${filterModel.quickFilterValues?.[0]?.trim()}%` },
      })),
    };
  }
  where = { ...where, ...like };

  return where;
};
const getFilter = filtersFetchData => {
  let filter = { ...filtersFetchData };
  return Object.keys(filter).length ? filter : null;
};

export interface IConfigOptions {
  verificationFetch?: string; //If fetch is in other endpoint
  withoutPagination?: boolean;
  withoutFilters?: boolean;
  withoutSavedFilters?: boolean; //DataGrid inside collapsable or modal
  withoutCancel?: boolean; //No cancelToken
  forDownload?: boolean;
}
export interface IPaginationModel {
  pageSize: number;
  page: number;
}
declare enum ELogicOperator {
  And = 'and',
  Or = 'or',
}
export interface IFilterItem {
  id?: number | string;
  field: string;
  value?: any;
  operator: string;
}
export interface IFilterModel extends GridFilterModel {
  items: IFilterItem[];
  //logicOperator?: ELogicOperator;
  quickFilterValues?: any[];
  //quickFilterLogicOperator?: ELogicOperator;
  quickFilterExcludeHiddenColumns?: boolean;
}

const transformOperator = (operator: string): TOperator => {
  switch (operator) {
    case '>':
      return 'gt';
    case '>=':
      return 'gte';
    case '<':
      return 'lt';
    case '<=':
      return 'lte';
    case '=':
      return 'equals';
    case '!=':
      return 'noEquals';
    case 'isAnyOf':
      return 'isAnyOf';
    case 'isEmpty':
      return 'isEmpty';
    default:
      return operator as TOperator;
  }
};

const transformFilterModelToWhereV2 = (componentSettings: ICompSetting, filterModel?: IFilterModel, options?: IConfigOptions) => {
  const where = {};
  if (options?.withoutSavedFilters) return where;
  const listToWhere = filterModel?.items.filter(
    item =>
      item.field === 'id' ||
      item.field === 'customId' ||
      item.field === 'name' ||
      item.field === 'elementType' ||
      item.field === 'eventType' ||
      item.field === 'defaultLocation' ||
      item.field === 'createdAt' ||
      item.field === 'timestamp' ||
      item.field === 'lastActivity'
  );
  listToWhere?.forEach(item => {
    const operator = item.operator.split('_')[0];
    if (!item.value) return;
    const quickFindColumns = componentSettings?.quickFindColumns;
    switch (item.field) {
      case 'id':
      case 'customId':
        if (quickFindColumns?.includes('customId')) where['customId'] = { $iLike: '%' + item.value + '%' };
        if (quickFindColumns?.includes('logicalId')) where['logicalId'] = { $iLike: '%' + item.value + '%' };
        if (quickFindColumns?.includes('id')) where['id'] = { $iLike: '%' + item.value + '%' };
        break;
      case 'name':
        if (quickFindColumns?.includes('name')) where['name'] = { $iLike: '%' + item.value + '%' };
        break;
      case 'eventType':
      case 'elementType':
      case 'defaultLocation':
        const formatValue: any[] = operator === 'isNot' || operator === 'isNotIn' ? { $not: item.value } : item.value;
        if (formatValue.length > 0) where[item.field + 'Id'] = formatValue;
        break;

      case 'createdAt':
      case 'timestamp':
      case 'lastActivity':
        where[item.field] = {
          ...where[item.field],
          ...((item.operator === 'after' || item.operator === 'onOrAfter') && { $gte: item.value }),
          ...((item.operator === 'before' || item.operator === 'onOrBefore') && { $lte: item.value }),
        };
        break;

      default:
        where[item.field + 'Id'] = item.value;
        break;
    }
  });

  return where;
};
const transformFilterModelToFilterV2 = (filterModel?: IFilterModel, options?: IConfigOptions) => {
  const filters = {};
  if (options?.withoutSavedFilters) return filters;

  const listToFilter = filterModel?.items.filter(
    item =>
      item.field === 'labels' ||
      item.field === 'flavors' ||
      item.field === 'xlocs' ||
      item.field === 'user' ||
      item.field === 'location' ||
      item.field === 'items' ||
      item.field === 'readingType' ||
      item.field === 'organization' ||
      item.field === 'usersEvent' ||
      item.field === 'relatedItemsEvents' ||
      item.field === 'locationEvent' ||
      item.field.startsWith('status.')
  );
  listToFilter?.forEach((item: IFilterItem) => {
    const operator = item.operator.split('_')[0];
    //const component = item.operator.split('_')[1];
    if (item.value === undefined) return;
    let formatValue = item.value;
    switch (item.field) {
      case 'labels':
        filters['tag'] = { id: Array.isArray(item.value) ? item.value : [item.value] };
        break;

      case 'flavors':
        filters[item.value] = { isAlive: true };
        break;

      case 'xlocs':
        formatValue =
          operator === 'isNot' || operator === 'isNotIn'
            ? { parentElementId: { $not: item.value } }
            : {
              parentElementId: item.value,
            };
        filters['xlocs'] = formatValue;
        break;

      case 'organization':
        filters['organization'] = {
          logicalId: item.value,
        };
        break;

      case 'location':
      case 'items':
      case 'readingType':
      case 'user':
        const dic = {
          location: 'xEventLocation',
          items: 'xEventItem',
          readingType: 'stream',
          user: 'xEventUser',
        };
        formatValue =
          operator === 'isNot' || operator === 'isNotIn'
            ? { [item.field + 'Id']: { $not: item.value } }
            : {
              [item.field + 'Id']: item.value,
            };
        filters[dic[item.field]] = formatValue;
        break;

      case 'relatedItemsEvents':
      case 'usersEvent':
      case 'locationEvent':
        const dicEvent = {
          location: 'xEventLocation',
          items: 'xEventItem',
          readingType: 'stream',
          user: 'xEventUser',
          usersEvent: 'userId',
          locationEvent: 'locationId',
          relatedItemsEvents: 'itemId',
        };
        filters[dicEvent[item.field]] = item.value;
        break;

      default:
        if (item.field.startsWith('status.')) {
          const indicatorTypeId = item.field.split('.')[1];
          filters['indicators'] = [
            ...(filters['indicators'] ?? []),
            {
              indicatorTypeId: indicatorTypeId,
              value: {
                [item.operator]: item.value,
              },
            },
          ];
        } else {
          filters[item.field] = item.value;
        }
        break;
    }
  });
  return filters;
};

//#region V3
type TSortV3 = { direction: 'ASC' | 'DESC'; column: string };
export interface ISearchBodyV3 {
  limit: number;
  offset: number;
  logicalOperator?: 'OR' | 'AND';
  sort?: TSortV3[];
  filters?: { [key: string]: CommonStringFiltersDto };
  isSearch?: boolean; //To diference fetch/get or post
}

const buildFiltersFromWhere = (where?: { [key: string]: any }) => {
  if (!where) {
    return undefined;
  }
  return Object.keys(where).reduce((acc, key) => {
    if (where[key]) {
      acc[key] = Array.isArray(where[key]) ? { isAnyOf: where[key] } : { equals: where[key] };
    }
    return acc;
  }, {});
};

const buildFilters = (filterModel?: IFilterModel) => {
  /* if (filterModel?.items.length === 0) {
    return undefined;
  } */
  const filters =
    filterModel?.items.reduce((acc, item) => {
      if (item.value !== undefined && item.value !== null) {
        const operator: TOperator = transformOperator(item.operator.split('_')[0] as TOperator);
        const component = item.operator.split('_')[1];

        const value = component === 'entitySelector' ? { id: { [operator]: item.value } } : { [operator]: item.value };
        acc[item.field] = value;
      }

      return acc;
    }, {}) ?? {};
  if (filterModel?.quickFilterValues?.[0]) {
    filters['id'] = { contains: `${filterModel.quickFilterValues?.[0]}` };
    filters['name'] = { contains: `${filterModel.quickFilterValues?.[0]}` };
  }
  return filters;
};

//#region Sort
type TSortModel = {
  order: 'ASC' | 'DESC';
  column: string;
};
const buildSort = (sortModel: GridSortModel | TSortModel | undefined): GridSortModel | undefined => {
  if (Array.isArray(sortModel)) {
    if (!sortModel || sortModel.length === 0) {
      return [];
    }
    return sortModel;
  } else {
    if (sortModel) {
      return [{ field: (sortModel as TSortModel).column, sort: (sortModel as TSortModel).order === 'ASC' ? 'asc' : 'desc' }];
    } else {
      return [];
    }
  }
};
const buildSortV3 = (sortModel?: GridSortModel): TSortV3[] => {
  if (!sortModel || sortModel?.length === 0) {
    return [];
  }
  return sortModel.map(item => {
    return {
      direction: item.sort === 'asc' ? 'ASC' : 'DESC',
      column: item.field,
    };
  });
};

//#endregion

//#endregion

export const getConfiguration = (
  configProps: IConfigComponent,
  settings: { componentSettings: ICompSetting; dataGridConfig?: IDataGridConfig },
  dataGridModels: {
    paginationModel?: IPaginationModel;
    filterModel?: IFilterModel;
    sortModel?: GridSortModel;
  },
  options?: IConfigOptions
) => {
  const { componentSettings, dataGridConfig } = settings;
  const { paginationModel, filterModel, sortModel } = dataGridModels;
  if (configProps.fetchURL?.startsWith('/api/v3')) {
    const anySort = [...(buildSort(settings.componentSettings.initialSort) ?? []), ...(buildSort(sortModel) ?? [])];
    const newSearchBody: ISearchBodyV3 = {
      limit: paginationModel?.pageSize ?? 0,
      offset: paginationModel ? paginationModel.page * paginationModel.pageSize : 0,
      logicalOperator: 'OR',
      filters: { ...buildFilters(filterModel), ...buildFiltersFromWhere(configProps.where) },
      sort: buildSortV3(anySort),
      isSearch: true,
    };

    return newSearchBody;
  } else {
    //V1 and V2
    const filters = {
      ...getFilter(configProps.filtersFetchData),
      ...transformFilterModelToFilterV2(filterModel),
    };
    const _sortModel = sortModel || dataGridConfig?.sortModel;

    return {
      //Pagination
      ...(options?.forDownload && { findAndCount: true, offset: 0 }),
      ...(!options?.withoutPagination && { findAndCount: true }),
      ...(paginationModel && !options?.withoutPagination && { offset: paginationModel.page * paginationModel.pageSize }),
      limit: options?.withoutPagination ? 10000 : paginationModel?.pageSize,
      //Fetch
      ...(!options?.withoutFilters && {
        where: {
          ...getWhere(configProps, componentSettings, filterModel),
          ...transformFilterModelToWhereV2(componentSettings, filterModel, options),
        },
      }),
      ...(filters && !options?.withoutFilters && { filters: filters }),
      //Sort
      ...((_sortModel?.[0]?.field || settings.componentSettings.initialSort) && {
        sortColumn: _sortModel?.[0]?.field ?? settings.componentSettings.initialSort?.column,
      }),
      ...((_sortModel?.[0]?.sort || settings.componentSettings.initialSort) && {
        sortOrder: _sortModel?.[0]?.sort ?? settings.componentSettings.initialSort?.order.toUpperCase(),
      }),
    };
  }
};
